import { z } from 'zod';
import { measurementUnitSchema } from '../constants/garment-types.js';
import { isoDateStringSchema, objectIdSchema } from './common.js';
import { measurementFieldKeySchema } from './measurements.js';
import { measurementSketchRefSchema } from './measurement-sketches.js';

/**
 * Captured measurements belonging to a customer.
 *
 * A shop measures the person standing at the counter, which is often before it has agreed to
 * anything with her — and she is a customer from the moment she is written down (somebody who has
 * only enquired is a customer with no orders yet), so a profile is owned by a customer and nothing
 * else. (The table's `lead_id` column is legacy: the lead → customer data migration moved every
 * enquiry's profiles onto customers. No API reads or writes it.) Note that this does not put
 * measurements on a QUOTATION — see `schemas/quotations.ts`: the customer carries the body, the
 * quotation the price.
 *
 * This is the feature that lets a shop say "we already have your measurements", which is the
 * single strongest reason a customer comes back — so the shape below is built around the two
 * things that make that promise trustworthy.
 *
 * The first is that a value belongs to a field KEY, never to a label. A shop that renames
 * "Bust" to "Chest round" has renamed a heading; the value captured three years ago is
 * untouched and still readable, because it was never stored against the words.
 *
 * The second is `PROFILE_SNAPSHOT_VERSION` and the snapshot shape at the bottom. Task 10
 * freezes a copy of these values onto an order item so that editing a customer's measurements
 * can never rewrite a completed order. That copy is produced by ONE service in the API and
 * described by ONE schema here, so the snapshot and the profile cannot drift apart.
 */

export const PROFILE_NAME_MAX_LENGTH = 80;
export const FIELD_NOTE_MAX_LENGTH = 200;
export const PROFILE_NOTES_MAX_LENGTH = 1000;

/**
 * A profile's name is free text on purpose. "Priya — blouse", "daughter — frock", "mummy".
 * A woman routinely orders for herself, her daughter and her mother on one phone number, and
 * only she knows which set is whose.
 */
export const profileNameSchema = z
  .string()
  .trim()
  .min(1, 'Give this set of measurements a name')
  .max(PROFILE_NAME_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/**
 * One captured value.
 *
 * `value` is the canonical number — `36 1/2` typed at the counter is stored as `36.5`, exactly,
 * because every tape-measure marking is a binary fraction. `enteredAs` keeps what was actually
 * typed, so a job card can print it back the way the shop wrote it rather than the way a
 * computer would.
 *
 * `text` carries a `select` or free-text field instead; exactly one of `value` and `text` is
 * meaningful for a given field, decided by the template's input type.
 */
export const measurementValueSchema = z.object({
  fieldKey: measurementFieldKeySchema,
  value: z.number().finite().nullable(),
  text: z.string().nullable(),
  /** What the counter typed, verbatim. Display only — never the source of truth. */
  enteredAs: z.string().nullable(),
  /** The unit this value was taken in, frozen at capture. */
  unit: measurementUnitSchema,
  /** The qualification tailors add: "loose at waist", "customer prefers 1 inch extra". */
  note: z.string().nullable(),
});
export type MeasurementValue = z.infer<typeof measurementValueSchema>;

/** A value as it arrives from the capture form, before the API canonicalises it. */
export const measurementValueInputSchema = z.object({
  fieldKey: measurementFieldKeySchema,
  /** As typed: `36 1/2`, `36½`, `36.5`, or a choice from a select field. */
  enteredAs: z.string().trim().max(60).nullish(),
  note: z.string().trim().max(FIELD_NOTE_MAX_LENGTH).nullish(),
});
export type MeasurementValueInput = z.input<typeof measurementValueInputSchema>;

/** A value the API could read but thinks is unlikely. A warning, never a refusal. */
export const measurementWarningSchema = z.object({
  fieldKey: z.string(),
  reason: z.enum(['out_of_range', 'unreadable', 'unknown_field', 'inactive_field']),
  message: z.string(),
});
export type MeasurementWarning = z.infer<typeof measurementWarningSchema>;

export const measurementProfileSchema = z.object({
  id: objectIdSchema,
  /**
   * The customer these measurements belong to. Every profile captured today has one; null only on
   * a legacy profile still sitting on an enquiry the lead → customer migration has not reached.
   */
  customerId: objectIdSchema.nullable(),
  /** The profile this one was copied from, if it started life as somebody else's reading. */
  copiedFromProfileId: objectIdSchema.nullable(),
  garmentTypeKey: z.string(),
  /** The template's label at the time of reading — for display, never for matching. */
  garmentTypeLabel: z.string(),
  name: z.string(),
  values: z.array(measurementValueSchema),
  notes: z.string().nullable(),
  /** Who took the measurements, and when they were last changed. */
  takenByUserId: z.string().nullable(),
  takenByName: z.string().nullable(),
  takenAt: isoDateStringSchema,
  /** Shown prominently, so a profile two years stale is visibly stale. */
  updatedAt: isoDateStringSchema,
  createdAt: isoDateStringSchema,
  /** Warnings from the last save — implausible values the shop chose to keep. */
  warnings: z.array(measurementWarningSchema),
  /**
   * The drawing that goes with these values, or null when nobody has drawn one. Defaulted, so a
   * response from an API that predates sketches reads as "no drawing" rather than failing.
   */
  sketch: measurementSketchRefSchema.nullable().default(null),
});
export type MeasurementProfile = z.infer<typeof measurementProfileSchema>;

export const measurementProfileListSchema = z.object({
  profiles: z.array(measurementProfileSchema),
});

/**
 * One capture with nothing in it about WHOSE it is — the garment, the name, the values, the note.
 *
 * Separated from the owner because there is a second caller that cannot name an owner yet: the
 * Add-a-customer form, which measures the person standing at the counter before the record she
 * will hang off exists. Her drafts travel inside `createCustomerSchema` and are written in the
 * same transaction as she is. One definition rather than two, so the counter cannot be asked
 * for a different set of things depending on which screen it happened to be standing on.
 */
export const capturedMeasurementsSchema = z.object({
  garmentTypeKey: z.string().min(1),
  name: profileNameSchema,
  values: z.array(measurementValueInputSchema).max(120).default([]),
  notes: z.string().trim().max(PROFILE_NOTES_MAX_LENGTH).nullish(),
  /** A sketch already saved through `/measurement-sketches`, to hang on this profile. */
  sketchId: objectIdSchema.nullish(),
});
export type CapturedMeasurementsInput = z.input<typeof capturedMeasurementsSchema>;

/**
 * A profile is created against a customer — always, and only. Strict, so a body still naming a
 * `leadId` is refused rather than quietly re-read.
 */
export const createProfileSchema = capturedMeasurementsSchema
  .extend({
    customerId: objectIdSchema,
    /** Duplicating an existing profile as the starting point for a new one. */
    duplicateOfProfileId: objectIdSchema.optional(),
    /**
     * Set when this profile is being copied from one the shop already holds. Kept so conversion
     * can tell an untouched copy (redundant) from one the counter re-measured over (a new
     * reading, and the reason the copy was worth making).
     */
    copiedFromProfileId: objectIdSchema.optional(),
  })
  .strict();
export type CreateProfileInput = z.input<typeof createProfileSchema>;

/** The owner side of a request, on its own — what the list query carries: one customer. */
export const profileOwnerQuerySchema = z.object({
  customerId: z.string().min(1, 'Ask for one customer'),
});

export const updateProfileSchema = z
  .object({
    name: profileNameSchema.optional(),
    values: z.array(measurementValueInputSchema).max(120).optional(),
    notes: z.string().trim().max(PROFILE_NOTES_MAX_LENGTH).nullish(),
    /**
     * A redraw: the id of a NEW sketch (sketches are never edited). Explicit null takes the
     * drawing off the profile; orders that froze it keep theirs.
     */
    sketchId: objectIdSchema.nullish(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateProfileInput = z.input<typeof updateProfileSchema>;

/** One recorded change to a profile, as the "what did we have before?" view renders it. */
export const profileRevisionSchema = z.object({
  occurredAt: isoDateStringSchema,
  actorName: z.string().nullable(),
  /** Dotted paths that differ, straight from the audit entry. */
  changedFields: z.array(z.string()),
  changes: z.array(
    z.object({
      fieldKey: z.string(),
      label: z.string(),
      before: z.string().nullable(),
      after: z.string().nullable(),
    }),
  ),
  /** Set when this change redrew (or removed) the sketch: both drawings, so both can be shown. */
  sketch: z
    .object({
      before: measurementSketchRefSchema.nullable(),
      after: measurementSketchRefSchema.nullable(),
    })
    .nullable()
    .default(null),
});
export type ProfileRevision = z.infer<typeof profileRevisionSchema>;

export const profileHistorySchema = z.object({ revisions: z.array(profileRevisionSchema) });

// --- The snapshot task 10 freezes onto an order item ----------------------------------------

/**
 * Bumped when the snapshot shape changes in a way a stored snapshot must be read differently
 * for. A frozen snapshot records the version it was written under, so an order from two years
 * ago is still interpretable after the shape moves on.
 */
export const PROFILE_SNAPSHOT_VERSION = 1;

/**
 * The exact shape task 10 copies onto an order item.
 *
 * It carries the LABELS as well as the keys, deliberately. A snapshot has to be readable years
 * later, in a dispute, when the template it came from may have been renamed, regrouped or
 * retired — so it copies enough to print a job card with no lookup at all. That redundancy is
 * the point of a snapshot; a snapshot that needed the live template to be understood would not
 * be one.
 */
export const measurementSnapshotSchema = z.object({
  version: z.literal(PROFILE_SNAPSHOT_VERSION),
  profileId: objectIdSchema,
  profileName: z.string(),
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  /** When the values were taken — not when the order was booked. */
  takenAt: isoDateStringSchema,
  takenByName: z.string().nullable(),
  /** The moment the copy was made. */
  capturedAt: isoDateStringSchema,
  notes: z.string().nullable(),
  values: z.array(
    z.object({
      fieldKey: z.string(),
      label: z.string(),
      group: z.string().nullable(),
      value: z.number().finite().nullable(),
      text: z.string().nullable(),
      /** Formatted the way the shop wrote it, so a job card prints without any parsing. */
      display: z.string(),
      unit: measurementUnitSchema,
      note: z.string().nullable(),
      sortOrder: z.number().int(),
    }),
  ),
  /**
   * The drawing, frozen by reference: a sketch row never changes, so pointing at it IS a copy.
   * Absent on snapshots written before sketches existed, which is why it is optional rather than
   * a reason to bump {@link PROFILE_SNAPSHOT_VERSION}. A per-order redraw replaces it here only.
   */
  sketch: measurementSketchRefSchema.nullish(),
});
export type MeasurementSnapshot = z.infer<typeof measurementSnapshotSchema>;
